import { useMemo } from "react";
import { ListIllustration } from "vega-illustrations";

import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { type EmptyCardProps } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import { InformativeModal } from "@vega-core/components/Modals/InformativeModal/InformativeModal";
import { Switch } from "@vega-core/components/Switch/Switch";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";

import { ShareTabType } from "../../constants/sharePageConstants";
import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { useExportFilename } from "../../hooks/useExportFilename";
import { useShareTableColumns } from "../../hooks/useShareTableColumns";
import { useShareTableManagement } from "../../hooks/useShareTableManagement";
import { useTableExport } from "../../hooks/useTableExport";
import { OpportunityOwnerModal } from "../ProspectsTable/OpportunityOwnerModal";
import { ShareTableToolbar } from "../ShareTable/ShareTableToolbar";

type SharePendingTableProps = {
  canAddShortlistItems?: boolean;
  canApprove?: boolean;
  onFindFromSalesforce: () => void;
  clearSelectionTrigger: number;
  onUploadClick?: () => void;
};

export const SharePendingTable = ({
  canAddShortlistItems,
  canApprove,
  onFindFromSalesforce,
  clearSelectionTrigger,
  onUploadClick,
}: SharePendingTableProps) => {
  const {
    dealId,
    currency,
    prospectsData,
    paginationElement,
    isLoading,
    searchQuery,
    setSearchQuery,
    RenderFilters,
    rowSelection,
    onRowSelectionChange,
    showOnlyAddedByMe,
    setShowOnlyAddedByMe,
    showSelectedOnly,
    SelectOnlyToggle,
    approvingProspectId,
    handleApproveSingleProspect,
    showApprovalModal,
    closeApprovalModal,
    isApprovingProspects,
    handleApproveProspects,
    showRemoveConfirmation,
    selectedProspectForRemoval,
    handleRemoveIndividualProspect,
    handleShowIndividualRemoveConfirmation,
    closeRemoveConfirmation,
    isOpportunityOwnerModalOpen,
    selectedProspectsForOwner,
    closeOpportunityOwnerModal,
    handleOpenOwnerModal,
    bulkActions,
    showEmptyState,
    handleCellEdit,
  } = useShareTableManagement(ShareTabType.Pending, {
    canApproveDealShortlist: canApprove,
    clearSelectionTrigger,
  });

  const { isMutationLocked, isClosedDeal, isCancelledDeal } =
    useCoInvestDealReadOnly();

  const gridEmptyState: EmptyCardProps | undefined = useMemo(() => {
    if (!showEmptyState) return undefined;

    if (isCancelledDeal) {
      return {
        title: m.coInvestments_prospects_emptyState_cancelledTitle(),
        description:
          m.coInvestments_prospects_emptyState_cancelledDescription(),
        illustration: <ListIllustration />,
      };
    }

    if (isClosedDeal) {
      return {
        title: m.coInvestments_prospects_emptyState_closedTitle(),
        description: m.coInvestments_prospects_emptyState_closedDescription(),
        illustration: <ListIllustration />,
      };
    }

    return {
      title: m.coInvestments_addProspectsToYourList(),
      description: m.coInvestments_addProspectsDescription(),
      illustration: <ListIllustration />,
      actions: !isMutationLocked ? (
        <div className="flex gap-2 mt-2">
          {onUploadClick ? (
            <ThemedButton
              dataTestId="upload-csv-button-empty-state"
              onClick={onUploadClick}
              size="small"
              variant="secondary"
            >
              {m.common_uploadCsv()}
            </ThemedButton>
          ) : null}
          <ThemedButton
            dataTestId="find-from-salesforce-button-empty-state"
            onClick={onFindFromSalesforce}
            size="small"
            variant="primary"
          >
            {m.coInvestments_prospects_emptyState_importFromSalesforce()}
          </ThemedButton>
        </div>
      ) : undefined,
    };
  }, [
    showEmptyState,
    isClosedDeal,
    isCancelledDeal,
    onUploadClick,
    onFindFromSalesforce,
    isMutationLocked,
  ]);

  const { columns, onCellKeyDown, renderColumnToggle, exportColumns } =
    useShareTableColumns({
      activeTab: ShareTabType.Pending,
      dealId,
      canAddShortlistItems,
      canApprove,
      currency,
      onApproveProspect: handleApproveSingleProspect,
      approvingProspectId,
      onRemoveProspect: handleShowIndividualRemoveConfirmation,
      removingProspectId: selectedProspectForRemoval,
      onOpenOwnerModal: handleOpenOwnerModal,
    });

  const exportFilename = useExportFilename(dealId, "prospects");
  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: prospectsData,
    columns: exportColumns,
    filename: exportFilename,
  });

  return (
    <>
      <div className="flex flex-col gap-4">
        {!showEmptyState && (
          <>
            <div className="flex gap-6">
              <Tooltip
                content={
                  showSelectedOnly
                    ? m.coInvestments_prospects_filterDisabledWhileShowingSelected()
                    : undefined
                }
              >
                <span>
                  <Switch
                    checked={showOnlyAddedByMe}
                    disabled={showSelectedOnly}
                    label={m.coInvestments_prospects_addedByMe()}
                    labelClassName="text-sm"
                    onChange={setShowOnlyAddedByMe}
                  />
                </span>
              </Tooltip>
              {!isMutationLocked && SelectOnlyToggle}
            </div>
            <ShareTableToolbar
              exportTestId="pending-export-menu"
              isExporting={isExporting}
              onExportCsv={exportToCsv}
              onExportExcel={exportToExcel}
              onSearchQueryChange={setSearchQuery}
              renderColumnToggle={renderColumnToggle}
              renderFilters={RenderFilters}
              searchDisabled={showSelectedOnly}
              searchName="pending-search"
              searchQuery={searchQuery}
            />
          </>
        )}

        <DataGrid<DealShortlistItemResponse>
          columnDefs={columns}
          emptyState={gridEmptyState}
          getRowId={(params) => params.data.id}
          loading={isLoading}
          onCellKeyDown={onCellKeyDown}
          onCellValueChanged={handleCellEdit}
          onSelectedRowsChange={onRowSelectionChange}
          rowData={prospectsData}
          rowSelectionOptions={{
            isRowSelectable: (node) =>
              !isMutationLocked && !node.data?.isApproved,
          }}
          selectedRows={rowSelection}
        />
        {paginationElement}
      </div>

      <InformativeModal
        description={m.coInvestments_prospects_remove_confirmModal_description()}
        isOpen={showRemoveConfirmation}
        onClose={closeRemoveConfirmation}
        primaryBtnProps={{
          children: m.btn_continue(),
          onClick: () => {
            if (selectedProspectForRemoval) {
              handleRemoveIndividualProspect(selectedProspectForRemoval);
            }
          },
          variant: "primary",
        }}
        secondaryBtnProps={{
          children: m.btn_cancel(),
          onClick: closeRemoveConfirmation,
        }}
        title={m.coInvestments_prospects_remove_confirmModal_title()}
        variant="error"
      />

      <InformativeModal
        description={m.coInvestments_approveProspectList_description()}
        isOpen={showApprovalModal}
        onClose={closeApprovalModal}
        primaryBtnProps={{
          children: m.btn_continue(),
          onClick: handleApproveProspects,
          variant: "primary",
          isLoading: isApprovingProspects,
          disabled: isApprovingProspects,
        }}
        secondaryBtnProps={{
          children: m.btn_cancel(),
          onClick: closeApprovalModal,
          disabled: isApprovingProspects,
        }}
        title={m.coInvestments_approveProspectList_title()}
        variant="success"
      />

      {isOpportunityOwnerModalOpen ? (
        <OpportunityOwnerModal
          closeModal={closeOpportunityOwnerModal}
          dealId={dealId}
          open={isOpportunityOwnerModalOpen}
          selectedProspects={selectedProspectsForOwner}
        />
      ) : null}

      {bulkActions}
    </>
  );
};
